import { UserType } from "../types";

// These are our event names
export enum SegmentTrackMetricEnum {
  USER_VIEWED = "User Viewed",
  USER_CLICKED = "User Clicked",
  FORM_SUBMITTED = "Form Submitted",
  USER_INPUTTED = "User Inputted",
}

// These are our event types
export enum EventTypeEnum {
  CLICK_RECORDING_TOGGLE = "Recording toggle clicked",
  CLIENT_INFO = "Client Info",
  CLIENT_MOOD_CHECKIN = "Client Mood Check-in",
  CLIENT_MOOD_CHECKIN_SUBMITTED = "Client Mood Check-in Submitted",
  CLIENT_WAITING_ROOM = "Telehealth Client Waiting Room",
  GAVE_TRANSCRIPTION_CONSENT = "User Consented to Transcription",
  LINK_CLICKED = "Link Clicked",
  MEASURE_PROGRESS_CLICKED = "Measure Progress Clicked",
  MEASURE_PROGRESS_VIEWED = "Measure Progress Viewed",
  MEASURE_VIEWED = "Telehealth Measure Viewed",
  PRE_SESSION_VIBES = "Pre Session Vibes",
  PROVIDER_END_WHITEBOARD = "Provider End Whiteboard",
  PROVIDER_NUDGE_CLICKED = "Provider Nudge Clicked",
  PROVIDER_SESSION_EARLY_END_COMPLETE = "Provider Ended Session Early (Complete)",
  PROVIDER_SESSION_EARLY_END_RESTART = "Provider Ended Session Early (Restart)",
  PROVIDER_SESSION_COMPLETE = "Provider Completed Session",
  PROVIDER_START_WHITEBOARD = "Provider Start Whiteboard",
  PROVIDER_WAITING_ROOM = "Telehealth Provider Waiting Room",
  REFLECTION_TOPICS_VIEWED = "Reflection Topics Viewed",
  SCHEDULE_TAB = "Schedule Tab",
  SCRIBBLE_ACCESSED = "Scribble (Patient Note) Accessed",
  SCRIBBLE_EDITED = "Scribble (Patient Note) Edited",
  TREATMENT_TAB = "Treatment Tab",
  WAITING_ROOM_APPOINTMENT_CREATED = "Waiting Room Appointment Created",
  WAITING_ROOM_APPOINTMENT_UPDATED = "Waiting Room Appointment Updated",
  WAITING_ROOM_SCHEDULE = "Waiting Room Schedule",
  FIVE_MINUTE_REMINDER_TOAST_SCHEDULING_PROMPT = "5 Minute Reminder Toast Scheduling Prompt",
}

export type TrackParams = {
  cta: string;
  patientShortId: string;
  providerShortId: string;
  numberOfAppointments: number;
  scheduleError: boolean;
  appointmentPosition: number;
  appointmentStatus: "RESCHEDULED" | "CANCELLED";
  appointmentShortId: string;
  appointmentTimeStart: string;
  newRecordingStatus: "PLAY" | "PAUSE";
  entityType: UserType;
  entityShortId: string;
  type: string;
  measureProgressView: "table" | "graph";
  inSession?: boolean;
  isViewingUpcomingClient?: boolean;
  nudgeId: number;
  editedLength: number;
  paragraphCount: number;
};

export type ClientInfoParams = Pick<
  TrackParams,
  "patientShortId" | "inSession" | "isViewingUpcomingClient"
>;
export type WaitingRoomAppointmentUpdatedParams = Pick<
  TrackParams,
  "patientShortId" | "appointmentShortId" | "appointmentStatus"
>;

export type WaitingRoomAppointmentCreatedParams = Pick<
  TrackParams,
  "patientShortId" | "appointmentShortId"
>;

export type MoodCheckinFormViewedParams = Pick<TrackParams, "patientShortId">;
export type MoodCheckinFormSubmittedParams = Pick<
  TrackParams,
  "patientShortId"
>;

export type WaitingRoomScheduleViewParams = Pick<
  TrackParams,
  "numberOfAppointments" | "scheduleError"
>;
export type WaitingRoomScheduleClickParams = Pick<
  TrackParams,
  "appointmentPosition" | "appointmentShortId"
>;
export type WaitingRoomViewParams = Pick<
  TrackParams,
  "appointmentShortId" | "appointmentTimeStart"
>;

export type MeasureViewedParams = Pick<TrackParams, "type">;

export type RecordingToggleClickedParams = Pick<
  TrackParams,
  "appointmentShortId" | "newRecordingStatus" | "entityType"
>;

export type TranscriptionConsentClickedParams = Pick<
  TrackParams,
  "appointmentShortId" | "entityType"
>;

export type MeasureProgressClickedParams = Pick<
  TrackParams,
  "measureProgressView" | "inSession"
>;

export type MeasureProgressViewedParams = Pick<
  TrackParams,
  "measureProgressView" | "inSession"
>;

export type PreSessionVibesViewedParams = Pick<
  TrackParams,
  "patientShortId" | "providerShortId" | "appointmentShortId"
>;

export type WhiteboardParams = Pick<TrackParams, "appointmentShortId">;

export type ProviderNudgeClickedParams = Pick<TrackParams, "nudgeId">;

export type ReflectionTopicsViewedParams = Pick<
  TrackParams,
  "patientShortId" | "inSession"
>;

export type ScribbleAccessedParams =
  | Pick<TrackParams, "patientShortId" | "appointmentShortId" | "inSession">
  | Pick<TrackParams, "patientShortId">;

export type ScribbleEditedParams = ScribbleAccessedParams &
  Pick<TrackParams, "editedLength" | "paragraphCount">;

export type LinkClickedParams = Pick<TrackParams, "cta">;

export type EndSessionParams = Pick<TrackParams, "appointmentShortId">;
export type FiveMinuteReminderToastParams = Pick<
  TrackParams,
  "patientShortId" | "providerShortId"
>;

export type EventToViewParamsMap = {
  [EventTypeEnum.CLIENT_INFO]: ClientInfoParams;
  [EventTypeEnum.CLIENT_MOOD_CHECKIN]: MoodCheckinFormViewedParams;
  [EventTypeEnum.WAITING_ROOM_SCHEDULE]: WaitingRoomScheduleViewParams;
  [EventTypeEnum.CLIENT_WAITING_ROOM]: WaitingRoomViewParams;
  [EventTypeEnum.PROVIDER_WAITING_ROOM]: WaitingRoomViewParams;
  [EventTypeEnum.MEASURE_VIEWED]: MeasureViewedParams;
  [EventTypeEnum.MEASURE_PROGRESS_VIEWED]: MeasureProgressViewedParams;
  [EventTypeEnum.PRE_SESSION_VIBES]: PreSessionVibesViewedParams;
  [EventTypeEnum.SCRIBBLE_ACCESSED]: ScribbleAccessedParams;
  [EventTypeEnum.REFLECTION_TOPICS_VIEWED]: ReflectionTopicsViewedParams;
};

export type UserViewedParams<P extends EventTypeEnum> = {
  componentName?: string;
  componentType?: string;
} & (P extends keyof EventToViewParamsMap ? EventToViewParamsMap[P] : never);

export type EventToClickParamsMap = {
  [EventTypeEnum.WAITING_ROOM_SCHEDULE]: WaitingRoomScheduleClickParams;
  [EventTypeEnum.CLIENT_INFO]: ClientInfoParams;
  [EventTypeEnum.CLICK_RECORDING_TOGGLE]: RecordingToggleClickedParams;
  [EventTypeEnum.GAVE_TRANSCRIPTION_CONSENT]: TranscriptionConsentClickedParams;
  [EventTypeEnum.MEASURE_PROGRESS_CLICKED]: MeasureProgressClickedParams;
  [EventTypeEnum.PROVIDER_NUDGE_CLICKED]: ProviderNudgeClickedParams;
  [EventTypeEnum.PROVIDER_START_WHITEBOARD]: WhiteboardParams;
  [EventTypeEnum.PROVIDER_END_WHITEBOARD]: WhiteboardParams;
  [EventTypeEnum.LINK_CLICKED]: LinkClickedParams;
  [EventTypeEnum.PROVIDER_SESSION_EARLY_END_COMPLETE]: EndSessionParams;
  [EventTypeEnum.PROVIDER_SESSION_EARLY_END_RESTART]: EndSessionParams;
  [EventTypeEnum.PROVIDER_SESSION_COMPLETE]: EndSessionParams;
  [EventTypeEnum.FIVE_MINUTE_REMINDER_TOAST_SCHEDULING_PROMPT]: FiveMinuteReminderToastParams;
};

export type UserClickedParams<P extends EventTypeEnum> =
  P extends keyof EventToClickParamsMap ? EventToClickParamsMap[P] : never;

export type EventToFormSubmittedParamsMap = {
  [EventTypeEnum.CLIENT_MOOD_CHECKIN_SUBMITTED]: MoodCheckinFormSubmittedParams;
  [EventTypeEnum.WAITING_ROOM_APPOINTMENT_UPDATED]: WaitingRoomAppointmentUpdatedParams;
  [EventTypeEnum.WAITING_ROOM_APPOINTMENT_CREATED]: WaitingRoomAppointmentCreatedParams;
};

export type FormSubmittedParams<P extends EventTypeEnum> =
  P extends keyof EventToFormSubmittedParamsMap
    ? EventToFormSubmittedParamsMap[P]
    : never;

export type EventToInputtedParamsMap = {
  [EventTypeEnum.SCRIBBLE_EDITED]: ScribbleEditedParams;
};

export type UserInputtedParams<P extends EventTypeEnum> =
  P extends keyof EventToInputtedParamsMap
    ? EventToInputtedParamsMap[P]
    : never;
